
import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { carMakes, carLogos, carModels, years } from '@/utils/carData';
import { engineTypes } from '@/components/oil-finder/CarForm';

interface CarDetailsSectionProps {
  formData: {
    carMake: string;
    carModel: string;
    carYear: string;
    engineType: string;
  };
  onInputChange: (field: string, value: string) => void;
}

const CarDetailsSection = ({ formData, onInputChange }: CarDetailsSectionProps) => {
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [engineDropdownOpen, setEngineDropdownOpen] = useState(false);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Car Make */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          ماركة السيارة <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          className="input-field text-right flex items-center justify-between w-full"
          onClick={() => setMakeDropdownOpen(!makeDropdownOpen)}
        >
          <div className="flex items-center">
            {formData.carMake && carLogos[formData.carMake] && (
              <img 
                src={carLogos[formData.carMake]} 
                alt={`${formData.carMake} logo`} 
                className="h-6 w-6 ml-2 object-contain" 
              />
            )}
            <span>{formData.carMake || 'اختر ماركة السيارة'}</span>
          </div>
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </button>
        {makeDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
            {carMakes.map(make => (
              <div
                key={make}
                className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                onClick={() => {
                  onInputChange('carMake', make);
                  onInputChange('carModel', ''); // Reset model when make changes
                  setMakeDropdownOpen(false);
                }}
              >
                <div className="flex items-center">
                  {carLogos[make] && (
                    <img 
                      src={carLogos[make]} 
                      alt={`${make} logo`} 
                      className="h-6 w-6 ml-2 object-contain" 
                    />
                  )}
                  <span>{make}</span>
                </div>
                {formData.carMake === make && <Check className="h-4 w-4 text-autocare-blue" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Car Model */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          موديل السيارة <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          className={`input-field text-right flex items-center justify-between w-full ${
            !formData.carMake ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
          onClick={() => formData.carMake && setModelDropdownOpen(!modelDropdownOpen)}
          disabled={!formData.carMake}
        >
          <span>{formData.carModel || 'اختر موديل السيارة'}</span>
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </button>
        {modelDropdownOpen && formData.carMake && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
            {carModels[formData.carMake]?.map(model => (
              <div
                key={model}
                className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                onClick={() => {
                  onInputChange('carModel', model);
                  setModelDropdownOpen(false);
                }}
              >
                {model}
                {formData.carModel === model && <Check className="h-4 w-4 text-autocare-blue" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Car Year */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          سنة الصنع <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          className="input-field text-right flex items-center justify-between w-full"
          onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
        >
          <span>{formData.carYear || 'اختر سنة الصنع'}</span>
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </button>
        {yearDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
            {years.map(year => (
              <div
                key={year}
                className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                onClick={() => {
                  onInputChange('carYear', year.toString());
                  setYearDropdownOpen(false);
                }}
              >
                {year}
                {formData.carYear === year.toString() && <Check className="h-4 w-4 text-autocare-blue" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Engine Type */}
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          نوع المحرك <span className="text-red-500">*</span>
        </label>
        <button
          type="button"
          className="input-field text-right flex items-center justify-between w-full"
          onClick={() => setEngineDropdownOpen(!engineDropdownOpen)}
        >
          <span>{formData.engineType || 'اختر نوع المحرك'}</span>
          <ChevronDown className="h-5 w-5 text-gray-400" />
        </button>
        {engineDropdownOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
            {engineTypes.map(engine => (
              <div
                key={engine}
                className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                onClick={() => {
                  onInputChange('engineType', engine);
                  setEngineDropdownOpen(false);
                }}
              >
                {engine}
                {formData.engineType === engine && <Check className="h-4 w-4 text-autocare-blue" />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarDetailsSection;
